import {useEffect} from 'react';
import {useForm, Controller} from 'react-hook-form';
import {zodResolver} from '@hookform/resolvers/zod';
import {usePage} from '@inertiajs/react';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
} from '@/components/ui/dialog';
import {
    Form,
    FormField,
    FormItem,
    FormLabel,
    FormControl,
    FormMessage,
} from '@/components/ui/form';
import {Input} from '@/components/ui/input';
import {Textarea} from '@/components/ui/textarea';
import {Button} from '@/components/ui/button';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import PatientAutocomplete from '@/components/patients/PatientAutocomplete';
import {Doctor, Patient} from '@/lib/types/models.ts';
import {EventFormPayload, EventSchema} from '@/lib/types/forms.ts';
import DatePicker from "@/components/ui/DatePicker.tsx";
import {Plus} from "lucide-react";

interface CreateEventDialogProps {
    isOpen: boolean;
    onOpenChange: (open: boolean) => void;
    onCreate: (data: EventFormPayload) => Promise<void>;
    doctors: Doctor[];
    start: string;
    end: string;
    doctor_id?: string | null;
    patient_id?: number | null;
    onCreatePatient: () => void;
    selectedPatient?: Patient | null;
    onPatientChange?: (patient: Patient | null) => void;
}

export default function CreateEventDialog({
                                              isOpen,
                                              onOpenChange,
                                              onCreate,
                                              doctors,
                                              start,
                                              end,
                                              doctor_id,
                                              patient_id = null,
                                              onCreatePatient,
                                              selectedPatient = null,
                                              onPatientChange,
                                          }: CreateEventDialogProps) {
    const {translations} = usePage().props as unknown as { translations: any };
    const t = translations.create_dialog;

    const form = useForm<EventFormPayload>({
        resolver: zodResolver(EventSchema),
        defaultValues: {
            name: '',
            description: '',
            start_at: start,
            end_at: end,
            event_type: 'appointment',
            doctor_id: doctor_id ?? '',
            patient_id: selectedPatient?.id ?? patient_id ?? undefined,
        },
    });

    useEffect(() => {
        if (!isOpen) return;

        form.reset({
            name: selectedPatient?.name ?? '',
            description: '',
            start_at: start,
            end_at: end,
            event_type: 'appointment',
            doctor_id: doctor_id ?? '',
            patient_id: selectedPatient?.id ?? patient_id ?? undefined,
        });
    }, [isOpen, start, end, doctor_id, form]);

    useEffect(() => {
        form.setValue('start_at', start);
        form.setValue('end_at', end);
    }, [start, end, form]);

    useEffect(() => {
        form.setValue('doctor_id', doctor_id ?? '');
    }, [doctor_id, form]);

    useEffect(() => {
        if (!selectedPatient) return;

        form.setValue('patient_id', selectedPatient.id);
        form.setValue('name', selectedPatient.name);
    }, [selectedPatient, form]);

    const handleSubmit = async (data: EventFormPayload) => {
        await onCreate(data);
    };

    return (
        <Dialog open={isOpen} onOpenChange={onOpenChange}>
            <DialogContent
                className="max-h-[90vh] w-full sm:max-h-[90vh] sm:max-w-lg p-4 overflow-auto"
            >
                <DialogHeader>
                    <DialogTitle>{t.title}</DialogTitle>
                </DialogHeader>

                <Form {...form}>
                    <form
                        onSubmit={form.handleSubmit(handleSubmit)}
                        className="flex flex-col gap-3">
                        <FormField
                            control={form.control}
                            name='name'
                            render={({field}) => (
                                <FormItem>
                                    <FormLabel>{t.labels.name}</FormLabel>
                                    <FormControl>
                                        <Input {...field} placeholder={t.placeholders.name} />
                                    </FormControl>
                                    <FormMessage/>
                                </FormItem>
                            )}
                        />

                        {(['start_at', 'end_at'] as const).map((key) => (
                            <FormField
                                key={key}
                                control={form.control}
                                name={key}
                                render={({field}) => (
                                    <FormItem>
                                        <FormLabel>
                                            {key === 'start_at' ? t.labels.start : t.labels.end}
                                        </FormLabel>
                                        <FormControl>
                                            <DatePicker
                                                value={field.value}
                                                onChange={field.onChange}
                                            />
                                        </FormControl>
                                        <FormMessage/>
                                    </FormItem>
                                )}
                            />
                        ))}

                        <Controller
                            control={form.control}
                            name='doctor_id'
                            render={({field, fieldState}) => (
                                <FormItem>
                                    <FormLabel>{t.labels.doctor}</FormLabel>
                                    <FormControl>
                                        <Select
                                            value={field.value || ''}
                                            onValueChange={(val) => field.onChange(val)}
                                        >
                                            <SelectTrigger>
                                                <SelectValue placeholder={t.placeholders.select_doctor}/>
                                            </SelectTrigger>
                                            <SelectContent>
                                                {doctors.map((doc) => (
                                                    <SelectItem key={doc.id} value={doc.id}>
                                                        {doc.name}
                                                    </SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    </FormControl>
                                    <FormMessage>{fieldState?.error?.message}</FormMessage>
                                </FormItem>
                            )}
                        />

                        <Controller
                            control={form.control}
                            name='event_type'
                            render={({field, fieldState}) => (
                                <FormItem>
                                    <FormLabel>{t.labels.event_type}</FormLabel>
                                    <FormControl>
                                        <Select
                                            value={field.value || ''}
                                            onValueChange={(val) => field.onChange(val)}
                                        >
                                            <SelectTrigger>
                                                <SelectValue placeholder={t.placeholders.event_type}/>
                                            </SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value='appointment'>
                                                    {t.select_options.appointment}
                                                </SelectItem>
                                                <SelectItem value='surgery'>
                                                    {t.select_options.surgery}
                                                </SelectItem>
                                            </SelectContent>
                                        </Select>
                                    </FormControl>
                                    <FormMessage>{fieldState?.error?.message}</FormMessage>
                                </FormItem>
                            )}
                        />

                        <FormField
                            control={form.control}
                            name="patient_id"
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel>{t.labels.patient}</FormLabel>

                                    <div className="flex items-center gap-2">
                                        <div className="flex-1">
                                            <PatientAutocomplete
                                                value={selectedPatient ?? null}
                                                onChange={(p: Patient | null | undefined) => {
                                                    const nextPatient = p ?? null;
                                                    onPatientChange?.(nextPatient);

                                                    if (p) {
                                                        field.onChange(p.id);
                                                        form.setValue('name', p.name);
                                                    } else {
                                                        field.onChange(undefined);
                                                        form.setValue('name', '');
                                                    }
                                                }}
                                            />
                                        </div>

                                        <Button
                                            type="button"
                                            variant="ghost"
                                            size="icon"
                                            className="h-10 w-10 shrink-0"
                                            onClick={onCreatePatient}
                                            aria-label="Create patient"
                                        >
                                            <Plus className="h-4 w-4" />
                                        </Button>
                                    </div>

                                    <FormMessage />
                                </FormItem>
                            )}
                        />

                        <FormField
                            control={form.control}
                            name='description'
                            render={({field}) => (
                                <FormItem>
                                    <FormLabel>{t.labels.description}</FormLabel>
                                    <FormControl>
                                        <Textarea {...field} value={field.value ?? ''} rows={3}/>
                                    </FormControl>
                                </FormItem>
                            )}
                        />

                        <DialogFooter className='flex flex-col sm:flex-row gap-2'>
                            <Button variant='outline'
                                    type='button'
                                    className='w-full sm:w-auto'
                                    onClick={() => onOpenChange(false)}
                            >
                                {t.buttons.cancel}
                            </Button>
                            <Button type="submit"
                                    className="w-full sm:w-auto"
                            >
                                {t.buttons.create}
                            </Button>
                        </DialogFooter>
                    </form>
                </Form>
            </DialogContent>
        </Dialog>
    );
}
